import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Typography, Grid } from '@material-ui/core';
import { colors, DisplayItem, DefaultCard, DottedHr } from 'design-system';
import LargeCircleCheckIcon from '../../Icons/LargeCircleCheckIcon';

const useStyles = makeStyles({
  titleSeparator: {
    height: 20,
  },
  subTitle: {
    paddingTop: '13px',
    paddingBottom: '0px',
  },
  listItem: {
    padding: '20px 0 0 0 !important',
  },
  boldListItem: {
    padding: '8px 0 !important',
  },
  listItemCoverage: {
    padding: '8px 0 !important',
  },
  coverageSeparation: {
    paddingTop: 30,
  },
  divider: {
    height: 1,
    backgroundColor: colors.gallery,
  },
});

type CardQuotationProps = {
  title: string;
  subTitle?: string;
  defaultDataList?: DisplayItem[];
  boldDataList?: DisplayItem[];
  defaultFooter?: DisplayItem | null;
  decorateFooter?: DisplayItem | null;
};

type ListVariant = 'regular' | 'bold';
type ListVariantResult = {
  leftListVariant: 'body2' | 'caption';
  rightListVariant: 'h2' | 'body2';
  leftFooterVariant: 'body1' | 'caption';
  rightFooterVariant: 'h2' | 'body1';
};

// Returns an object with the typography variants for bold and regular variants
function getVariantResult(variant: ListVariant): ListVariantResult {
  if (variant === 'bold') {
    return {
      leftListVariant: 'body2',
      rightListVariant: 'h2',
      leftFooterVariant: 'body1',
      rightFooterVariant: 'h2',
    };
  }
  return {
    leftListVariant: 'caption',
    rightListVariant: 'body2',
    leftFooterVariant: 'caption',
    rightFooterVariant: 'h2',
  };
}

const CardQuotation = ({
  title,
  subTitle = '',
  defaultDataList = [],
  boldDataList = [],
  defaultFooter = null,
  decorateFooter = null,
}: CardQuotationProps): JSX.Element => {
  const classes = useStyles();

  function createListItems(
    data: DisplayItem[],
    hasDivider: boolean,
    variant: ListVariant,
  ): JSX.Element {
    const variants = getVariantResult(variant);
    const usedClass =
      variant === 'bold' ? classes.boldListItem : classes.listItem;

    return (
      <div data-testid="list-item">
        {data.map(
          (defaultData: DisplayItem): React.ReactNode => (
            <React.Fragment key={defaultData.Value}>
              <Grid
                container
                alignItems="center"
                className={usedClass}
                key={defaultData.Value}
              >
                {/* Left list element */}
                <Grid xs={6} item>
                  <Typography
                    variant={variants.leftListVariant}
                    color={variant === 'bold' ? 'primary' : 'secondary'}
                  >
                    {defaultData.Value}
                  </Typography>
                </Grid>

                {/* Right list element */}
                <Grid
                  xs={6}
                  item
                  container
                  alignItems="center"
                  justifyContent="flex-end"
                >
                  <Typography variant={variants.rightListVariant}>
                    {defaultData.Text}
                  </Typography>
                </Grid>
              </Grid>

              {/* Separator */}
              {hasDivider && <DottedHr />}
            </React.Fragment>
          ),
        )}
      </div>
    );
  }

  function createFooter(
    data: DisplayItem,
    variant: ListVariant,
    hasDecoration: boolean,
    ignorePadding = false,
  ): JSX.Element {
    const variants = getVariantResult(variant);

    return (
      <Grid
        container
        style={{ paddingTop: ignorePadding ? 0 : 30 }}
        data-testid="footer"
        justifyContent="center"
      >
        <Grid xs={8} item container alignItems="center">
          {hasDecoration && (
            <>
              <LargeCircleCheckIcon />
              <div style={{ display: 'inline', width: 10 }} />
            </>
          )}
          <Typography variant={variants.leftFooterVariant}>
            {data.Value}
          </Typography>
        </Grid>
        <Grid
          xs={4}
          item
          container
          alignItems="center"
          justifyContent="flex-end"
        >
          <Typography variant={variants.rightFooterVariant}>
            {data.Text}
          </Typography>
        </Grid>
      </Grid>
    );
  }
  return (
    <DefaultCard>
      {title && (
        <Typography variant="body1" data-testid="title">
          {title}
        </Typography>
      )}
      {subTitle && (
        <Typography
          id="sales-web-label-cardquotationsubtitle"
          variant="h3"
          className={classes.subTitle}
          data-testid="subTitle"
        >
          {subTitle}
        </Typography>
      )}
      {!subTitle && <div className={classes.titleSeparator} />}
      {defaultDataList.length > 0 &&
        createListItems(defaultDataList, true, 'regular')}
      {boldDataList.length > 0 && createListItems(boldDataList, false, 'bold')}
      {defaultFooter && createFooter(defaultFooter, 'bold', false)}
      {decorateFooter &&
        createFooter(
          decorateFooter,
          'regular',
          true,
          defaultDataList.length <= 0 && boldDataList.length <= 0,
        )}
    </DefaultCard>
  );
};

export default CardQuotation;
